'use client';

import { useTranslations } from 'next-intl';

import { Link } from '@/components/Link';
import LocationOpeningLabel from '@/components/LocationOpeningLabel';
import { LocationDto } from '@/lib/locations/dto/location.dto';
import { Countries } from '@/models/Countries.enum';
import {
  getLocationHighlightName,
  shouldShowOpeningLabel,
} from '@/utils/locations';

import LocationCtas from './LocationCtas';

import clsx from 'clsx';

type StickyHeaderProps = {
  location: LocationDto;
  directionUrl: string;
  className?: string;
};

export default function StickyHeader({
  location,
  directionUrl,
  className,
}: StickyHeaderProps) {
  const t = useTranslations('location');

  const showOpeningLabel = shouldShowOpeningLabel({
    hideOpeningDate: location.hideOpeningDate,
    firstDayOfBusinessISO: location.firstDayOfBusinessISO,
    phase: location.phase,
  });

  return (
    <div
      className={clsx(
        'fixed top-[70px] left-0 right-0 z-[3] shadow-md flex items-center w-full bg-white animate-slideInDown py-4',
        className
      )}
    >
      <div className="flex items-center justify-between w-full gap-5 px-5 sm:px-10 xl:base-container">
        <div className="flex-1">
          {location.salesHighlightBanner && (
            <p
              className="px-5 py-1 mb-1 text-sm font-bold bg-yellow-400 rounded w-fit"
              data-testid="location-sales-highlight-banner"
            >
              {location.salesHighlightBanner}
            </p>
          )}
          <div className="flex flex-1 min-w-0 xl:items-center xl:gap-4 lg:flex-col xl:flex-row xl:text-lg">
            <h1
              data-testid="location-title"
              className="flex flex-col font-normal truncate tablet-landscape:gap-2 tablet-landscape:items-center tablet-landscape:flex-row"
            >
              <strong>
                {getLocationHighlightName({
                  name: location.name,
                  brandName: location.brandName,
                  t,
                })}
              </strong>
            </h1>
            <Link
              className="flex-none text-base underline"
              href={directionUrl}
              target="_blank"
            >
              {`${location.address}, ${
                location.country === Countries.France
                  ? `${location.zipCode} `
                  : ''
              }${location.city}, ${location.state}`}
            </Link>
          </div>
          {showOpeningLabel && (
            <LocationOpeningLabel
              className="not-italic font-semibold"
              openingDateISO={location.firstDayOfBusinessISO}
              locationGeolocalization={location.geolocalization}
            />
          )}
        </div>

        <LocationCtas
          className="grid grid-cols-2 gap-5 max-lg:hidden"
          location={location}
        />
      </div>
    </div>
  );
}
